import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import RecurrencePicker from "@/components/RecurrencePicker";

// Change an existing event's repeat pattern. Only future dates change; dates with registrations are kept.
export default function SeriesPatternEditor({ event, testid = "series-pattern" }) {
  const [rule, setRule] = useState(event.recurrence?.freq ? event.recurrence : null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const start = event.date > today ? event.date : today;
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/events/${event.id}/series-pattern`, { recurrence: rule });
      setResult(data);
      toast.success(`Pattern updated: ${data.added} added, ${data.removed} removed`);
      qc.invalidateQueries();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-xs text-[#4A5D6B]">{event.series_id ? "Change how this series repeats." : "Turn this into a repeating event."} Only dates from today on change; past dates and dates with registrations stay as they are.</p>
      <RecurrencePicker value={rule} onChange={setRule} startDate={start} testid={`${testid}-picker`} />
      <button type="button" onClick={apply} disabled={!rule || busy} data-testid={`${testid}-apply`} className="eyw-btn-outline !py-2 text-sm disabled:opacity-50">
        {busy && <Loader2 size={14} className="animate-spin" />} Apply to future dates
      </button>
      {result && (
        <p className="text-xs text-[#4A5D6B]" data-testid={`${testid}-result`}>
          {result.added} date{result.added === 1 ? "" : "s"} added, {result.removed} removed.
          {result.kept_with_registrations?.length > 0 && ` Kept ${result.kept_with_registrations.length} date(s) that already have registrations: ${result.kept_with_registrations.join(", ")}.`}
        </p>
      )}
    </div>
  );
}
